﻿// #region: Class definition *********************************************************************/
/**
 * This class contains the core javascript functions for the 'next' LeanForms version.
 */
window.leanformsNext = new class {

    // #region: Private Fields *******************************************************************/
    #loginMessageShown;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * The constructor does work that needs to be executed exactly once.
     */
    constructor() {

        // Magic numbers.
        this.DEFAULT_GRIDCOLUMN_WIDTH = 150;
        this.MIN_GRIDCOLUMN_WIDTH = 40;
        
        // References for offline user/groups lists.
        this.offlineUserList;
        this.offlineGroupList;
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Identifies class.
     * @returns {string}                        The string representation of this class.
     */
    toString() {
        return "[leanformsNext]";
    }

    static toString() {
        return "[leanformsNext]";
    }

    /** 
     * Identifies the version.
     * @returns {string}                        The version number of this class.
     */
    static version() {
        return "1.0.0";
    }
    // #endregion ********************************************************************************/

    // #region: Events ***************************************************************************/
    /**
     * Show the login message or an error toast.
     * @param {Event} e                         The event data provided from the error handler in an ajax call.
     */
    onAjaxError = (e, ...args) => {
        const event = e.xhr || e.XMLHttpRequest || e;
        const loginRequired = (event.status === 200 && event.statusText === "parsererror") || event.status === 401;

        if (loginRequired) {
            return this.showLoginMessage();
        }

        // Parse for a message sent from the server but ignore any error pages sent (contains a body tag). Check if we're offline.
        const responseText = (window.navigator.onLine ? null : resources.noNetworkMessage) ?? event.responseText ?? "";
        const contentType = event?.getResponseHeader?.("content-type");

        // Default to 'html' that will show the default error message.
        const isHtml = contentType?.includes("html") ?? true;
        const isJson = contentType?.includes("json");

        // Ignore any error pages sent. Using null for the message will show the default error to the user.
        if (isHtml) {
            return this.handleError({ message: null, e, ...args });
        }

        const errorMessage = isJson ? JSON.parse(responseText) : responseText;

        return this.handleError({ message: typeof errorMessage === "string" ? errorMessage : null, e, ...args });
    }

    /**
     * Displays an error notification.
     */
    handleError = ({ message, ...args } = {}) => {
        // Ignore large exception content.
        core.notify("error", message?.toLowerCase().indexOf("exception") > -1 ? null : message);

        console.log(message ?? "no-message", args ?? "no-args");
        if (args?.[0] instanceof Error) {
            throw args[0];
        }
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Set column widths for a given grid and an array of widths (that is saved for the user).
     * @param {HTMLElement} gridReference       The (Kendo) grid that should have the widths applied.
     * @param {Array} gridWidthsArray           The array with the widths values, defaults to an empty array.
     */
    applyColumnsWidths(gridReference, gridWidthsArray = []) {
        const headerColumns = gridReference.querySelector('.k-grid-header-wrap').querySelector('colgroup').querySelectorAll('col');
        const contentColumns = gridReference.querySelector('.k-grid-content').querySelector('colgroup').querySelectorAll('col');
        const visibleColumsCount = headerColumns.length;

        for (var i = 0; i < visibleColumsCount; i++) {
            const savedWidth = +gridWidthsArray[i];

            // Do not change width when explictly set in code.
            if (!savedWidth && headerColumns[i].style.width !== '') {
                continue;
            }

            // Use the widest value.
            const columnWidth = Math.max(this.MIN_GRIDCOLUMN_WIDTH, savedWidth || this.DEFAULT_GRIDCOLUMN_WIDTH);

            // Set width for both header and content columns.
            headerColumns[i].style.width = `${columnWidth}px`;
            contentColumns[i].style.width = `${columnWidth}px`;
        }
    }

    /**
     * Opens a dialog where a schedule can be edited.
     * @param {Object} targetWindow             The target window.
     * @param {String} title                    The window title.
     * @param {Integer} scheduleId              The schedule identifier.
     * @param {Integer} interval                The schedule interval.
     * @param {Integer} reminderDays            The schedule reminder days.
     */
    editSchedule(targetWindow, title, scheduleId, interval, reminderDays) {
        var $ = targetWindow.$;

        if ($("#scheduler-dialog").length) {
            // Already has an open window.
            return;
        }

        $("body").css("overflow", "hidden");
        $("#windowcontainer").append('<div id="scheduler-dialog"></div>');
        $("#scheduler-dialog").kendoWindow({
            position: {
                top: $(targetWindow).scrollTop() + 20,
                left: "20%"
            },
            width: coreUI.getPopupWidth(600),
            // minHeight reduces resize flash on load.
            minHeight: 282,
            title: title,
            modal: true,
            iframe: false,
            content: `${baseUrl}/Dialogs/SchedulerDialog?id=${scheduleId}&interval=${encodeURIComponent(interval || '')}&reminder-days=${reminderDays || ''}`,
            close: function () { $("body").css("overflow", ""); },
            deactivate: function () {
                this.destroy();
            }
        });
    }

    /**
     * Get the groups from the stored users list.
     */
    getOfflineGroups() {
        return new Promise((resolve, reject) => {
            if (this.offlineGroupList) {
                return resolve(this.offlineGroupList);
            }

            indexedDatabase.organizationUnits.get("groups").then((groups) => {
                this.offlineGroupList = groups.value;

                resolve(this.offlineGroupList);
            }).catch(reject);
        });
    }

    /**
     * Get the users from the stored users list.
     */
    getOfflineUsers() {
        return new Promise((resolve, reject) => {
            if (this.offlineUserList) {
                return resolve(this.offlineUserList);
            }

            indexedDatabase.organizationUnits.get("users").then((users) => {
                this.offlineUserList = users.value;

                resolve(this.offlineUserList);
            }).catch(reject);
        });
    }

    /**
     * Gets the 
     * @param {Integer} groupId                 The id of the group to get the id from.
     */
    getOfflineUsersFromGroup(groupId) {
        return Promise.all([this.getOfflineGroups(), this.getOfflineUsers()])
                      .then((data) => {
                          const groups = data[0];
                          const users = data[1];
                          const group = groups.find((g) => {
                              return groupId === +g.value;
                          });
                      
                          return users.filter((user) => {
                              return group.userIds.indexOf(+user.value) > -1;
                          });
                      });
    }

    /**
     * Declares references/urls related to images (on the server).
     * @param {String} relativePath             A realtive path to use, defaults to '../'.
     * @returns {Object}                        The configuration for the editor.
     */
    getImageBrowserConfig(relativePath = "../") {
        return {
            transport: {
                read: {
                    url: `${baseUrl}/api/ImageBrowser`,
                    type: "GET",
                    dataType: "json"
                },
                destroy: {
                    url: `${baseUrl}/api/ImageBrowser`,
                    type: "DELETE"
                },
                thumbnailUrl: `${baseUrl}/api/ImageBrowser/thumbnail`,
                uploadUrl: `${baseUrl}/api/ImageBrowser/upload`,
                imageUrl: `${relativePath}api/ImageBrowser/image?path={0}`
            }
        };
    }

    /**
     * Get the client state locally or from the database (if allowed).
     * @param {String} key                      The reference to get the data for. Can be null for ignoring.
     * @param {Window} tabWindow                The tab window. Defaults to top.window
     * @param {Boolean} getFromUrl              True to get client state from URL.
     */
    async getState(key, tabWindow, getFromUrl) {
        if (enableSaveGridState && key) {
            const response = await core.fetch({ url: baseUrl + "/api/settings/state/" + key })
                .catch(leanformsNext.onAjaxError);

            if (response.ok) {
                return await response?.json();
            }
        }

        if (getFromUrl) {
            let clientState = core.getQueryParameter("client-state", tabWindow ?? top.window) ?? "";
            return clientState === "" ? "" : JSON.parse(decodeURIComponent(clientState));
        }
    }

    /**
     * Parses a given string to XML (jQuery).
     * @param {String} xmlString                The string to create actual XML from
     */
    loadXml(xmlString) {
        return $($.parseXML(xmlString));
    }

    /**
     * 
     * @param {Error} err
     */
    markNetworkError(err) {
        if (err instanceof TypeError) {
            // It's not guaranteed that this is actually caused by a connection issue
            // but thanks to browser differences it's hard to get closer than this.
            throw new Error("lf-failed-fetch");
        }
        else {
            throw err;
        }
    }

    /**
     * Create a preview for an attachment.
     * @param {Event} e - The trigger event.
     */
    previewAttachment(e) {
        const button = e.target.closest('.button');
        const attachmentContainer = button.closest('.attachment-container');
        const displayName = attachmentContainer.querySelector('.display-name');
        const attachmentsContainer = attachmentContainer.parentElement;

        // Avoid scrolling outer body when popup is open.
        document.body.style.overflow = 'hidden';

        // Find current index in the collection of previewable attachments.
        const previewableAttachments = [...attachmentsContainer.querySelectorAll('.attachment-container')].filter(element => {
            const fileName = element.querySelector('.display-name').textContent;
            return coreUI.isImage(fileName) || fileName.endsWith('pdf');
        });

        // How many elements.
        const attachmentsCount = previewableAttachments.length;

        // Where are we?
        let currentIndex = previewableAttachments.findIndex(container => container === attachmentContainer);

        /**
         * Load attachment content into the dialog.
         * @param {number} index - The index of the attachment to load.
         */
        const loadAttachmentContent = (index) => {
            if (index < 0 || index >= attachmentsCount) {
                return;
            }

            // Remove show. 
            indexDiv.classList.remove('show');

            currentIndex = index;
            const container = previewableAttachments[index];
            const fileName = container.querySelector('.display-name');

            // Update dialog content
            if (fileName.textContent.endsWith('pdf')) {
                contentContainer.innerHTML = `<iframe src="${baseUrl + "/Dialogs/PdfViewer?pdf=" + encodeURIComponent(fileName.href)}"></iframe>`;
            }
            else {
                const imageElement = container.querySelector('.attachment-image');
                contentContainer.innerHTML = `<img class="preview-image" src="${imageElement.src}" />`;
            }

            // Update the title of the window.
            kendoWindow.title(resources.attachmentPreview.preview + ': ' + fileName.textContent);

            // Only show when we have more than one attachment to display.
            if (attachmentsCount > 1) {
                // Update counter.
                indexDiv.innerHTML = `<span class="index">${currentIndex + 1}&nbsp;/&nbsp;${attachmentsCount}</span>`;

                // Update navigation visibility.
                prevButton.style.visibility = currentIndex > 0 ? 'visible' : 'hidden';
                nextButton.style.visibility = currentIndex < previewableAttachments.length - 1 ? 'visible' : 'hidden';

                // Force reflow.
                void indexDiv.offsetWidth;
                indexDiv.classList.add('show');
            }
        };

        // Create dialog element.
        const dialog = document.createElement('div');
        dialog.id = 'attachment-dialog';
        dialog.className = 'attachment-preview-dialog';

        // Add content container.
        const contentContainer = document.createElement('div');
        contentContainer.className = 'attachment-preview-content';
        dialog.appendChild(contentContainer);

        // Add navigation.
        const navigationDiv = document.createElement('div');
        navigationDiv.className = 'attachment-preview-navigation';
        navigationDiv.innerHTML = `
                <button type="button" class="nav nav-previous" title="${resources.attachmentPreview.previous}">
                    <span class="preview-icon lf-icon-arrow-back-ios"></span>
                </button>
                <button type="button" class="nav nav-next" title="${resources.attachmentPreview.next}">
                    <span class="preview-icon lf-icon-arrow-forward-ios"></span>
                </button>`;
        dialog.appendChild(navigationDiv);

        // Add attatachment index.
        const indexDiv = document.createElement('div');
        indexDiv.className = 'attachment-index';
        dialog.appendChild(indexDiv);

        // Attach navigation events.
        const prevButton = navigationDiv.querySelector('.nav-previous');
        const nextButton = navigationDiv.querySelector('.nav-next');

        if (prevButton) {
            prevButton.addEventListener('click', () => {
                if (currentIndex > 0) {
                    loadAttachmentContent(currentIndex - 1);
                }
            });
        }

        if (nextButton) {
            nextButton.addEventListener('click', () => {
                if (currentIndex < previewableAttachments.length - 1) {
                    loadAttachmentContent(currentIndex + 1);
                }
            });
        }
        
        // Create Kendo window.
        const kendoWindow = new kendo.ui.Window(dialog, {
            width: coreUI.getPopupWidth(900),
            height: coreUI.getPopupHeight(700),
            title: resources.attachmentPreview.preview + ': ' + displayName.textContent,
            visible: false,
            modal: true,
            resizable: true,
            iframe: false,
            animation: false,
            close: function () {
                document.body.style.overflow = '';
                document.removeEventListener('keydown', keydownHandler, true);
            },
            deactivate: function () {
                this.destroy();
            }
        });

        // Center and open
        kendoWindow.center().open();

        // Add keyboard navigation.
        const keydownHandler = (event) => {
            // Block all arrow keys from reaching the Kendo window.
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                
                // Handle left arrow navigation.
                if (event.key === 'ArrowLeft' && currentIndex > 0 && prevButton) {
                    loadAttachmentContent(currentIndex - 1);
                }
                // Handle right arrow navigation.
                else if (event.key === 'ArrowRight' && currentIndex < previewableAttachments.length - 1 && nextButton) {
                    loadAttachmentContent(currentIndex + 1);
                }

                return false;
            }
            else if (event.key === 'Escape') {
                kendoWindow.close ? kendoWindow.close() : kendoWindow.destroy();
            }
        };

        // Add keyboard handler with capture phase to intercept before Kendo.
        document.addEventListener('keydown', keydownHandler, true);

        // Initialize content.
        loadAttachmentContent(currentIndex);
    }

    /**
     * Saves the columns width string to the settings table.
     * @param {HTMLElement} gridReference       The (Kendo) grid that the widths are for.
     * @param {HTMLElement} gridWidthsInput     The hidden input to store the widths values.
     * @param {String} key                      The key to store the data to, typically the grid name/type.
     * @param {Integer} minWidth                The min width to use, defaults to 60.
     */
    saveColumnsWidths(gridReference, gridWidthsInput, key, minWidth = 0) {
        const cols = gridReference.find(".k-grid-header-wrap").find("colgroup col");

        const widthData = cols.map(function (i) {
            const columnWidth = $(this).width();
            return Math.max(columnWidth, minWidth);
        }).get().join(";");

        // Store local and on the server, when changed.
        if (gridWidthsInput.value !== widthData) {
            gridWidthsInput.value = widthData;
            $.ajax({
                url: baseUrl + "/api/settings/columns/" + key,
                type: "PUT",
                data: JSON.stringify(widthData),
                contentType: "application/json",
                error: leanformsNext.onAjaxError
            });
        }
    }

    /**
     * Saves the client state to the querystring, and to the database (if allowed).
     * @param {DataSource} dataSource           The Kendo Grid datasource to read the settings from.
     * @param {Window} tabWindow                The tab window. Can be null.
     * @param {String} key                      The reference to store the data for. Can be null to not save on teh server.
     */
    saveState(dataSource, tabWindow, key) {
        const clientState = {
            sort: dataSource.sort(),
            filter: dataSource.filter()
        };

        // Store on the server, when allowed.
        if (enableSaveGridState && key) {
            $.ajax({
                url: baseUrl + "/api/settings/state/" + key,
                type: "PUT",
                contentType: "application/json",
                data: JSON.stringify(clientState),
                error: leanformsNext.onAjaxError
            });
        }
        else {
            const page = dataSource.page();
            let parameter = null;

            if (page > 1) {
                clientState.page = page;
            }

            if (clientState.sort?.length > 0 || clientState.filter?.filters?.length > 0 || page > 1) {
                parameter = JSON.stringify(clientState);
            }

            // Save in querystring (for bookmarking and browser back/forward).
            if (isEmbed) {
                const pageUrl = core.getEmbedUrl();

                if (parameter) {
                    pageUrl.searchParams.set("client-state", parameter);
                } else {
                    pageUrl.searchParams.delete("client-state");
                }
                
                core.setEmbedUrl(pageUrl);
            } else {
                const newUrl = core.setQueryParameter(top.window.location.href, "client-state", parameter);
                top.window.history.replaceState(null, "", newUrl);
            }

            // Set in tab for history navigation.
            if (tabWindow) {
                const newTabUrl = core.setQueryParameter(tabWindow.location.href, "client-state", parameter);
                tabWindow.history.replaceState(null, "", newTabUrl);
            }
        }
    }

    /**
     * Show the user a message that the session has ended and the user needs to login again.
     * When the button is clicked the login view will be loaded in the top most window.
     */
    showLoginMessage() {
        if (!this.#loginMessageShown) {
            const message =
                `${resources.security.sessionExpired}` +
                `<button type="button" onclick="top.location.href='${baseUrl}/Login?ReturnUrl=${core.getPageQueryParameter()}'" class="button" style="margin-inline-start: 1rem;">` +
                `${resources.security.loginLabel}` +
                `</button >`;

            this.#loginMessageShown = true;
            return core.notify("warn", message, { closable: false, duration: 0, offsetBlock: 150, purify: false });
        }

        return;
    }

    /**
     * Parses a provided date/time filter for the 'contains' operator.
     * @param {EventData} e                     All Kendo 'FilterMenuInit' eventdata from the grid. 
     */
    updateSearchFilterMenu(e) {
        const grid = e.sender;
        var filterWindow = $(e.container);

        // First remove (*)any) existing handler.
        filterWindow.off("click", "button[type=submit]").on("click", "button[type=submit]", function (filterEvent) {
            var form = $(this.form);
            var filterType = form.find("select[data-role=dropdownlist]").eq(0).val();
            var selectedValue = form.find("input").eq(0).val();

            // We do not filter on empty values.
            if (selectedValue === '') {
                filterEvent.preventDefault();

                return;
            }

            const fieldHeader = grid.thead.find("th[data-field='" + e.field + "']")[0];
            const fieldType = grid.columns[fieldHeader?.cellIndex].type;
            const isDateField = ["date", "time", "datetime"].indexOf(fieldType) > -1;

            if (!isDateField) {
                return;
            }

            // Ignore the change event on the datepicker, so kendo does not parses a value to null (a single year only par example).
            filterWindow.on("change", "input", function (event) {
                event.preventDefault();
            });

            // Only do this on the contains operator.
            // On the report page, do this for all operators. The server side handling is different there.
            if (filterType !== "contains" && grid.element[0].id !== "divReportGrid") {
                return;
            }

            filterEvent.preventDefault();

            // Parse a date column (with a partial date for using with the 'contains' operators).
            // If we do not modify the filter Kendo will pass 'null' and thus the filter will be ignored.
            var dateKendoFilter = {
                field: e.field,
                operator: filterType,
                value: selectedValue
            };

            var currentFilters = grid.dataSource.filter();
            if (currentFilters) {
                var newFilters = currentFilters.filters.filter(function (filter) {
                    return filter.field !== dateKendoFilter.field;
                });
                newFilters.push(dateKendoFilter);
                currentFilters.filters = newFilters;
            }
            else {
                currentFilters = dateKendoFilter;
            }

            grid.dataSource.filter(currentFilters);
            grid.thead.find("th[data-field='" + e.field + "']").data("kendoFilterMenu").popup.close();
        });
    }

    /**
     * Updates the search filters for a given grid.
     * @param {any} grid                        The source grid (for the data).
     * @param {any} field                       The field (column) to operate on.
     * @param {any} operator                    The operator to use.
     * @param {any} value                       The value to compare against (the selected filter),
     * @returns {Object}                        The filtered data source.
     */
    updateSearchFilters(grid, field, operator, value) {
        const newFilter = { field: field, operator: operator, value: value };
        const dataSource = grid.dataSource;
        let filters = null;

        if (dataSource.filter() != null) {
            filters = dataSource.filter().filters;
        }

        if (filters == null) {
            filters = [newFilter];
        }
        else {
            var isNew = true;
            for (var index = 0; index < filters.length; index++) {
                if (filters[index].field == field) {
                    isNew = false;
                    break;
                }
            }

            if (isNew) {
                filters.push(newFilter);
            }
            else {
                filters[index] = newFilter;
            }
        }

        dataSource.filter(filters);
    }
    // #endregion ********************************************************************************/
}();
// #endregion ************************************************************************************/